import { CheckCircleTwoTone, RadioButtonUnchecked } from "@mui/icons-material";
import {
    Checkbox,
    FormControlLabel,
    InputLabel,
    TextField,
    styled,
} from "@mui/material";
import clsx from "clsx";
import { ChangeEvent } from "react";
import { useAppSelector } from "../../../redux/store";
import TooltipIcon from "../../../shared/component/tooltip-icon";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { DynamicLabelType } from "../enums/dynamic-labels";
import { UserPreferencesKeys } from "../interface/dto/user-preferences.dto";
import { tooltipsSelector } from "../redux/selectors/tooltips-selector";
interface IOwnProps {
    onChange: (label: UserPreferencesKeys, value: string | boolean) => void;
    value: string;
    isLoading: boolean;
    saveFileWithExtension: boolean;
}

const FileNameFormat = ({
    className,
    onChange,
    value,
    isLoading,
    saveFileWithExtension,
}: IWithClassName & IOwnProps) => {
    const tooltips = useAppSelector(tooltipsSelector);

    const fileNameFormatTooltip = tooltips.fileNameFormat;

    const handleDynamicLabels = (dynamicLabel: DynamicLabelType) =>
        !isLoading && onChange("fileNameFormat", `${value}[${dynamicLabel}]`);

    const saveExtensionHandler = ({
        target,
    }: ChangeEvent<HTMLInputElement>) => {
        !isLoading && onChange("saveFileWithExtension", target.checked);
    };

    return (
        <div className={className}>
            <div className="file-name-format">
                <div className="tooltip-container">
                    <InputLabel className="title">
                        1. Filename Format
                    </InputLabel>
                    <TooltipIcon content={fileNameFormatTooltip} />
                </div>
                <div className="save-files">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={saveFileWithExtension}
                                icon={
                                    <RadioButtonUnchecked className="custom-checkbox" />
                                }
                                checkedIcon={
                                    <CheckCircleTwoTone className="custom-checkbox" />
                                }
                                disableFocusRipple={true}
                                disableRipple={true}
                                disableTouchRipple={true}
                                onChange={saveExtensionHandler}
                            />
                        }
                        label="Save files with this extension:"
                    />
                    <TextField
                        disabled={!saveFileWithExtension}
                        hiddenLabel
                        variant="standard"
                        size="small"
                        onChange={({ target }) =>
                            !isLoading &&
                            onChange(
                                "fileNameFormat",
                                target.value.replace(/[^\w\s[\]().,-]/gi, ""),
                            )
                        }
                        value={value}
                    />
                </div>
                <div
                    className={clsx("dynamic-labels", {
                        disabled: !saveFileWithExtension,
                    })}
                >
                    <InputLabel
                        className="list-name"
                        onClick={() =>
                            handleDynamicLabels(DynamicLabelType.ListName)
                        }
                    >{`[${DynamicLabelType.ListName}]`}</InputLabel>
                    <InputLabel
                        className="list-code"
                        onClick={() =>
                            handleDynamicLabels(DynamicLabelType.ListCode)
                        }
                    >{`[${DynamicLabelType.ListCode}]`}</InputLabel>
                    <InputLabel
                        className="list-id"
                        onClick={() =>
                            handleDynamicLabels(DynamicLabelType.ListId)
                        }
                    >{`[${DynamicLabelType.ListId}]`}</InputLabel>
                </div>
                <InputLabel
                    className={clsx("last-updated", {
                        disabled: !saveFileWithExtension,
                    })}
                    onClick={() =>
                        handleDynamicLabels(DynamicLabelType.LastUpdated)
                    }
                >
                    {`[${DynamicLabelType.LastUpdated}]`}
                </InputLabel>
            </div>
        </div>
    );
};

export default styled(FileNameFormat)(({ theme }: IWithThemeProps) => ({
    ".file-name-format": {
        width: 476,
        height: 266,
        borderRadius: 20,
        backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
        boxSizing: "border-box",
        padding: theme?.spacing(2, 2, 7, 3.5),
    },
    ".title": {
        fontWeight: "bold",
        color: theme?.palette.dropdown.listColor,
        margin: theme?.spacing(2, 1, 1, 0),
    },
    ".tooltip-container": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    ".custom-checkbox": {
        color: theme?.palette.listsTable.checkBoxColor,
    },
    ".list-name, .list-code, .list-id, .last-updated": {
        width: "120px",
        marginTop: theme?.spacing(2),
        color: theme?.palette.dropdown.listColor,
        textAlign: "center",
        borderBottom: `1px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }`,
        cursor: "pointer",
    },
    ".dynamic-labels": {
        display: "flex",
        justifyContent: "space-between",
    },
    ".save-files": {
        display: "flex",
        alignItems: "baseline",
    },
    ".disabled": {
        opacity: 0.3,
        cursor: "default",
    },
}));
